"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import deepEqual from 'fast-deep-equal';
import { useDeepEffect } from '../../../../helpers/hooks/useDeepEffect.mjs';
import { useState, useReducer, useRef } from 'react';
import { useComboBoxDropdownContext } from '../../context/use-combobox-dropdown.mjs';
import { NavigationContext } from './use-navigation.mjs';

function payloadReducer(state, action) {
  const compactState = Object.entries(state).reduce((acc, [sourceId, payload]) => {
    if (!payload.options.length && !payload.fallbackOptions.length) {
      return acc;
    }
    return {
      ...acc,
      [sourceId]: payload
    };
  }, {});
  return {
    ...compactState,
    [action.sourceId]: {
      index: action.index,
      options: action.options,
      fallbackOptions: action.fallbackOptions
    }
  };
}
function NavigationContextProvider({ children }) {
  const [currentItem, setCurrentItem] = useState(null);
  const [payload, dispatch] = useReducer(payloadReducer, {});
  const { isOptionSelected, drillDownStackTop } = useComboBoxDropdownContext();
  const insertionCounter = useRef(0);
  useDeepEffect(() => {
    const itemOptions = getUnifiedOptionsList(payload);
    if (!itemOptions.length) {
      return;
    }
    if (currentItem === "drill-up") {
      setCurrentItem(itemOptions[0]);
      return;
    }
    if (!currentItem || itemOptions.indexOf(currentItem) == -1) {
      setCurrentItem(itemOptions[0]);
      return;
    }
    if (getOptionIndex(currentItem, itemOptions) == -1) {
      const selectedOption = itemOptions.find(isOptionSelected);
      if (selectedOption) {
        setCurrentItem(selectedOption);
      } else {
        const onlyOptions = getOnlyItemOptions(itemOptions);
        setCurrentItem(onlyOptions[0]);
      }
    }
  }, [payload]);
  const moveToPreviousItem = () => {
    if (currentItem === "drill-up") {
      return;
    }
    const itemOptions = getUnifiedOptionsList(payload);
    if (!currentItem) {
      setCurrentItem(itemOptions[itemOptions.length - 1]);
      return;
    }
    const idx = getOptionIndex(currentItem, itemOptions);
    if (idx === 0 && !!drillDownStackTop) {
      setCurrentItem("drill-up");
      return;
    }
    if (idx === 0) {
      return;
    }
    if (idx > 0) {
      const currentOption = itemOptions[idx - 1];
      setCurrentItem(currentOption);
      return;
    }
    setCurrentItem(itemOptions[itemOptions.length - 1]);
  };
  const moveToNextItem = () => {
    const itemOptions = getUnifiedOptionsList(payload);
    if (itemOptions.length === 0) {
      setCurrentItem(null);
      return;
    }
    if (!currentItem || currentItem === "drill-up") {
      setCurrentItem(itemOptions[0]);
      return;
    }
    const idx = getOptionIndex(currentItem, itemOptions);
    if (idx === itemOptions.length - 1) {
      return;
    }
    if (idx === -1) {
      setCurrentItem(itemOptions[0]);
    }
    if (idx < itemOptions.length - 1) {
      const currentOption = itemOptions[idx + 1];
      setCurrentItem(currentOption);
    }
  };
  const onOptionsListChange = ({
    sourceId,
    options,
    fallbackOptions
  }) => {
    insertionCounter.current++;
    dispatch({
      sourceId,
      index: insertionCounter.current,
      options,
      fallbackOptions
    });
  };
  return /* @__PURE__ */ jsx(
    NavigationContext.Provider,
    {
      value: {
        currentItem,
        moveToNextItem,
        moveToPreviousItem,
        onOptionsListChange
      },
      children
    }
  );
}
const makeOptionComparable = (option) => {
  return option && {
    ...option,
    label: option.searchLabel ?? option.label?.toString()
  };
};
const getOnlyItemOptions = (options) => options.filter((o) => !o.isSection);
function getOptionIndex(option, options) {
  return options.findIndex((o) => deepEqual(makeOptionComparable(o), makeOptionComparable(option)));
}
const getUnifiedOptionsList = (payload) => {
  const { options, fallbackOptions } = Object.entries(payload).sort(([_, { index }]) => index).reduce(
    (acc, [_, { options: options2, fallbackOptions: fallbackOptions2 }]) => {
      return {
        options: [...acc.options, ...options2],
        fallbackOptions: [...acc.fallbackOptions, ...fallbackOptions2]
      };
    },
    {
      options: [],
      fallbackOptions: []
    }
  );
  const itemOptions = getOnlyItemOptions(options);
  return itemOptions.length ? itemOptions : getOnlyItemOptions(fallbackOptions);
};

export { NavigationContextProvider };
